import type { GameContext } from '../core/context';
import { clamp } from '../core/math';
import type { System } from '../core/system';
import type { ParticleService } from '../vehicles/fx/particles';

/**
 * Game-feel ("juice") helpers. Everything here is presentation-only:
 *
 * - `Trauma` / `shakeNoise`: pure trauma-based shake maths used by the camera rig.
 * - `HitStop`: a tiny real-time timer that asks for a brief slow-down after a big crash.
 * - `createJuiceSystem`: listens to physics events and adds the extra impact flash, dust and heavy
 *   shake/hit-stop. It never writes to physics, vehicles or the event bus.
 *
 * Hit-stop choice: the fixed-step simulation is untouched (each step is still exactly FIXED_DT and
 * produces identical results). We only scale `loop.timeScale` — the pace at which real time is
 * converted into those steps — for 40-90 ms, so the world appears to hesitate on impact. It only
 * engages while `timeScale === 1` (never fights pause slow-mo / `__bh.setTimeScale`), is skipped
 * under automation (`navigator.webdriver`, unless `?hitstop`), and is off for `?online` sessions,
 * where wall-clock pacing must stay locked to the server tick. `stepFrames` ignores it entirely.
 */

export const TRAUMA_DECAY = 1.4; // per second
export const TRAUMA_MAX = 1;

/** Trauma accumulator: add() stacks and caps, update() decays linearly, amount() is trauma². */
export class Trauma {
  value = 0;
  add(amount: number): void {
    this.value = clamp(this.value + amount, 0, TRAUMA_MAX);
  }
  update(dt: number): void {
    this.value = Math.max(0, this.value - dt * TRAUMA_DECAY);
  }
  /** Shake magnitude 0..1 (squared for a soft falloff). */
  amount(): number {
    return this.value * this.value;
  }
}

function hash(n: number): number {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return (s - Math.floor(s)) * 2 - 1;
}

/** Smoothed 1D value noise in -1..1, deterministic in (t, seed). */
export function shakeNoise(t: number, seed: number): number {
  const i = Math.floor(t);
  const f = t - i;
  const u = f * f * (3 - 2 * f);
  return hash(i + seed * 17.13) * (1 - u) + hash(i + 1 + seed * 17.13) * u;
}

/** Camera trauma for a collision: scales with closing speed, falls off with distance to the focus. */
export function impactTrauma(speed: number, distance: number, mine: boolean): number {
  if (speed <= 3) return 0;
  const base = Math.min(0.75, (speed - 3) / 22);
  if (mine) return base;
  const falloff = clamp(1 - distance / 45, 0, 1);
  return speed > 8 ? base * falloff * 0.8 : 0;
}

export const HITSTOP_MIN_SPEED = 11; // m/s closing speed
export const HITSTOP_SCALE = 0.06;

/** Duration (s) of the hit-stop for a closing speed; 0 below the threshold, 40..90 ms above it. */
export function hitStopDuration(speed: number): number {
  if (speed < HITSTOP_MIN_SPEED) return 0;
  return 0.04 + 0.05 * clamp((speed - HITSTOP_MIN_SPEED) / 14, 0, 1);
}

/** Real-time hit-stop timer with a cooldown so chained scrapes can't lock the game in slow-mo. */
export class HitStop {
  private remaining = 0;
  private cooldown = 0;
  trigger(duration: number): boolean {
    if (duration <= 0 || this.cooldown > 0) return false;
    this.remaining = Math.max(this.remaining, duration);
    this.cooldown = duration + 0.35;
    return true;
  }
  update(realDt: number): void {
    this.remaining = Math.max(0, this.remaining - realDt);
    this.cooldown = Math.max(0, this.cooldown - realDt);
  }
  get active(): boolean {
    return this.remaining > 0;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Enable/disable the crash hit-stop (presentation-only slow-down). */
    setHitStop(enabled: boolean): void;
  }
}

/** Game-feel helpers for other modules (weapons: a beat of hit-stop when a blow or a shot lands). */
export interface JuiceService {
  /** Request a hit-stop of `seconds` (same rules as crash hit-stop: cooldown, off under automation). */
  hitStop(seconds: number): void;
}

declare module '../core/services' {
  interface ServiceMap {
    juice: JuiceService;
  }
}

export function createJuiceSystem(): System {
  let ctx: GameContext;
  let particles: ParticleService | undefined;
  const hitStop = new HitStop();
  const STOP_SCALE = HITSTOP_SCALE;
  let enabled = true;
  let engaged = false;

  const search = typeof location !== 'undefined' ? location.search : '';
  const automated = typeof navigator !== 'undefined' && navigator.webdriver === true;
  if (/[?&]online\b/.test(search) || /[?&]nohitstop\b/.test(search)) enabled = false;
  else if (automated && !/[?&]hitstop\b/.test(search)) enabled = false;

  return {
    id: 'juice',
    order: 735,
    init(c) {
      ctx = c;
      c.hooks.register('setHitStop', (e) => {
        enabled = e;
      });
      c.services.provide('juice', {
        hitStop(seconds) {
          if (enabled) hitStop.trigger(seconds);
        },
      });
      c.events.on('collision', (e) => {
        const focus = c.services.tryGet('cameraRig')?.lookAt;
        const pid = c.services.tryGet('player')?.vehicle?.id ?? null;
        const mine = (e.a.kind === 'vehicle' && e.a.id === pid) || (e.b?.kind === 'vehicle' && e.b.id === pid) || e.a.kind === 'player';
        if (!e.isNew || e.speed < 6) return;
        const d = focus ? Math.hypot(e.pos.x - focus.x, e.pos.z - focus.z) : 0;
        if (!mine && d > 45) return;
        // Extra punch on top of what the vehicle system emits: a bright flash and a low dust ring.
        const at = { x: e.pos.x, y: 0.7, z: e.pos.z };
        const k = clamp(e.speed / 16, 0.4, 1.4);
        particles?.emit('impactFlash', at, { count: 1, intensity: k });
        particles?.emit('dust', at, { count: Math.min(8, 2 + Math.round(e.speed / 4)), intensity: k });
        if (e.speed > 9) particles?.emit('sparks', at, { count: 6, dir: { x: e.normal.x, z: e.normal.z }, intensity: k });
        if (mine && enabled) hitStop.trigger(hitStopDuration(e.speed));
      });
    },
    start() {
      particles = ctx.services.tryGet('particles');
    },
    update(dt) {
      // `dt` here is real frame time, so the timer is independent of the slowed simulation pace.
      hitStop.update(dt);
      const loop = ctx.loop;
      if (hitStop.active && enabled) {
        if (!engaged && loop.timeScale === 1) {
          loop.timeScale = STOP_SCALE;
          engaged = true;
        }
      } else if (engaged) {
        if (loop.timeScale === STOP_SCALE) loop.timeScale = 1;
        engaged = false;
      }
    },
    dispose() {
      if (engaged && ctx.loop.timeScale === STOP_SCALE) ctx.loop.timeScale = 1;
    },
  };
}
